// === DARK THEME COLORS ===
const dark = {
  bg: { page: "#09090b", container: "#1c1c1e", card: "#27272a", header: "#27272a" },
  text: { primary: "#f1f5f9", secondary: "#94a3b8", title: "#e4e4e7", accent: "#4ade80" },
  border: { main: "#3f3f46", light: "#27272a" },
  accent: { primary: "#4ade80", dark: "#16a34a" },
};

// === LIGHT THEME COLORS ===
const light = {
  bg: { page: "#ffffff", container: "#ffffff", card: "#f8fafc", header: "#f8fafc" },
  text: { primary: "#1e293b", secondary: "#64748b", title: "#0f172a", accent: "#16a34a" },
  border: { main: "#e2e8f0", light: "#f1f5f9" },
  accent: { primary: "#16a34a", dark: "#15803d" },
};

const muiSytle = {
  // === THEME COLORS (export for use) ===
  dark,
  light,

  // === HELPER FUNCTIONS ===
  // Get accordion styles based on dark mode
  getAccordion: (isDark) => ({
    my: 1,
    border: 1,
    borderColor: isDark ? dark.border.main : light.border.main,
    borderRadius: "4px",
    bgcolor: isDark ? dark.bg.container : light.bg.container,
    "&:before": { display: "none" },
  }),

  // Get accordion title typography based on dark mode
  getAccordionTitle: (isDark) => ({
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: 500,
    width: 1,
    paddingX: 3,
    paddingY: 0,
    textAlign: "left",
    color: isDark ? dark.text.accent : "black",
    "&:hover": { color: isDark ? dark.text.primary : "grey" },
  }),

  // Get page title typography based on dark mode
  getPageTitle: (isDark) => ({
    fontFamily: "Poppins",
    fontSize: "30px",
    fontWeight: "500",
    color: isDark ? dark.text.primary : "black",
    textAlign: "left",
  }),

  // Get page subtitle typography based on dark mode
  getPageSubtitle: (isDark) => ({
    fontFamily: "Poppins",
    fontSize: "15px",
    fontWeight: "100",
    color: isDark ? dark.text.secondary : "#4b5563",
    textAlign: "left",
  }),

  // Get table title typography based on dark mode
  getTableTitle: (isDark) => ({
    color: isDark ? dark.text.primary : "#333333",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "20px",
  }),

  // Get paper/card styles based on dark mode
  getPaper: (isDark) => ({
    bgcolor: isDark ? dark.bg.container : light.bg.container,
    border: `1px solid ${isDark ? dark.border.main : light.border.main}`,
    color: isDark ? dark.text.primary : light.text.primary,
  }),

  // Get main container styles based on dark mode
  getMainContainer: (isDark) => ({
    padding: 3,
    bgcolor: isDark ? dark.bg.page : light.bg.page,
  }),

  // === STATIC STYLES (original) ===
  accordion: {
    my: 1,
    border: 1,
    borderColor: "#e0e0e0",
    borderRadius: "4px",
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    paddingX: 0,
    minHeight: "0px",
    paddingY: 1,
    "&.MuiButtonBase-root .MuiAccordionSummary-content": {
      margin: 0,
      minHeight: "0px",
      padding: 0,
    },
  },
  accordionSummaryTypography: {
    fontFamily: "Poppins",
    fontSize: "18px",
    fontWeight: 500,
    width: 1,
    paddingX: 3,
    paddingY: 0,
    textAlign: "left",
    color: "inherit",
    "&:hover": { color: "grey" },
  },
  accordionDetails: { padding: 0 },
  smallGreenIconButton: {
    textTransform: "none",
    backgroundColor: "#15a448",
    color: "white",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#73c891",
    },
    width: "24px",
    height: "24px",
    padding: 0.4,
  },
  secondaryTab: {
    textTransform: "none",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 400,
    "&.Mui-selected": {
      color: "#15a448",
      fontWeight: 450,
    },
    "&.MuiButtonBase-root": {
      paddingY: 0,
      paddingX: 1,
      minHeight: "1px",
      minWidth: "1px",
    },
    "&:hover": {
      color: "#15a448",
    },
  },
  secondaryTabs: {
    "&.MuiTabs-root": {
      minHeight: "1px",
    },
    "&.MuiTabs-root .MuiTabs-indicator": {
      backgroundColor: "#15a440",
    },
  },
  secondaryTabDisabled: {
    paddingX: 0,
    width: "1px",
    minWidth: "1px",
    "&.MuiButtonBase-root": {
      paddingY: 0,
      paddingX: 0.75,
      minHeight: "1px",
      minWidth: "1px",
    },
  },
  secondaryTabDivider: {
    backgroundColor: "#e0e0e0",
  },
  tabTitleContainer: {
    backgroundColor: "#f1f1f1",
  },
  primaryTab: {
    display: "flex",
    flexGrow: 1,
  },
  primaryTabs: {
    flexGrow: 1,
  },
  virginMaterialColor: "rgba(185, 134, 16, 0.1)",
  reusedMaterialColor: "rgba(16, 185, 129, 0.1)",
  mainContainer: { padding: 3 },
  pageTitleContainer: {
    padding: 1,
    display: "flex",
    justifyContent: "space-between",
  },
  typographyPageTitle: {
    fontSize: "30px",
    fontWeight: "500",
    color: "inherit",
    textAlign: "left",
  },
  getTypographyPageTitle: (isDark) => ({
    fontFamily: "Poppins",
    fontSize: "30px",
    fontWeight: "500",
    color: isDark ? dark.text.title : "black",
    textAlign: "left",
  }),
  typographyPageSubtitle: {
    fontFamily: "Poppins",
    fontSize: "15px",
    fontWeight: "100",
    color: "inherit",
    textAlign: "left",
  },
  tableContainer: {
    border: 1,
    borderColor: "#e0e0e0",
    borderRadius: 3,
    overflow: "hidden",
  },
  getTableContainer: (isDark) => ({
    border: 1,
    borderColor: isDark ? dark.border.main : "#e0e0e0",
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: isDark ? dark.bg.container : "white",
  }),
  tableHeaderContainer: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  tableTitleContainer: {
    paddingY: 1,
    paddingX: 3,
    display: "flex",
    gap: 3,
    alignItems: "center",
  },
  typographyTableTitle: {
    color: "inherit",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "20px",
  },
  getTypographyTableTitle: (isDark) => ({
    color: isDark ? dark.text.accent : "#333333",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: "20px",
  }),
  tableButtonContainer: { paddingY: 2, paddingX: 2, display: "flex", gap: 1, alignItems: "center" },
  mainColor: "#16a34a",
  mainBackgroundColor: "rgba(22, 163, 74, 0.1)",
  chartContainer: {
    height: "600px",
    minWidth: "600px",
    flex: 1,
    borderRadius: 4,
    overflow: "hidden",
  },
  commonContainer: { border: 1, borderColor: "lightgray", borderRadius: 2, padding: 2 },
};

export default muiSytle;
